import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  LinearProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import {
  COMPANY_TYPES,
  EMPLOYEE_RANGES,
  type CompanyType,
  type EmployeeRange,
  type Client,
} from '@altyor-kronos/shared-types';
import { enrichClientInfo } from '../../core/data/adminMutations';
import { colors } from '../../core/theme/theme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ClientFormData {
  name: string;
  description: string;
  logoURL: string;
  website: string;
  linkedinURL: string;
  industry: string;
  companyType: CompanyType | '';
  employeeRange: EmployeeRange | '';
  country: string;
  city: string;
  foundedYear: string;
  parentCompany: string;
  tags: string[];
}

interface Props {
  open: boolean;
  editing?: { id: string; data: Partial<Client> } | null;
  allClients: { id: string }[];
  onSubmit: (values: ClientFormData) => Promise<void>;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Cute loading messages that rotate while AI searches
// ---------------------------------------------------------------------------

const LOADING_MESSAGES = [
  'Searching the web for company info...',
  'Reading annual reports and press releases...',
  'Checking LinkedIn and industry databases...',
  'Digging through Wikipedia and Crunchbase...',
  'Almost there, polishing the results...',
  'This can take up to 30 seconds, hang tight!',
  'Cross-referencing multiple sources...',
  'Finding the perfect logo...',
  'Still working on it, good things take time...',
];

function useRotatingMessage(active: boolean, messages: string[], intervalMs = 3000): string {
  const [index, setIndex] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval>>(undefined);

  useEffect(() => {
    if (!active) {
      setIndex(0);
      return;
    }
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % messages.length);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [active, messages.length, intervalMs]);

  return messages[index];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_FORM: ClientFormData = {
  name: '',
  description: '',
  logoURL: '',
  website: '',
  linkedinURL: '',
  industry: '',
  companyType: '',
  employeeRange: '',
  country: '',
  city: '',
  foundedYear: '',
  parentCompany: '',
  tags: [],
};

function clientToForm(data: Partial<Client>): ClientFormData {
  return {
    name: data.name ?? '',
    description: data.description ?? '',
    logoURL: data.logoURL ?? '',
    website: data.website ?? '',
    linkedinURL: data.linkedinURL ?? '',
    industry: data.industry ?? '',
    companyType: data.companyType ?? '',
    employeeRange: data.employeeRange ?? '',
    country: data.country ?? '',
    city: data.city ?? '',
    // Firestore stores cleared optional fields as null — treat like undefined.
    foundedYear: data.foundedYear != null ? String(data.foundedYear) : '',
    parentCompany: data.parentCompany ?? '',
    tags: data.tags ?? [],
  };
}

/**
 * Merge an enrichment result into the current form: AI values win, existing
 * values fill the gaps. In the create flow's search phase the current form is
 * empty, so this degrades to a plain replace (no behavior change there).
 */
function mergeForms(current: ClientFormData, enriched: ClientFormData): ClientFormData {
  return {
    name: enriched.name || current.name,
    description: enriched.description || current.description,
    logoURL: enriched.logoURL || current.logoURL,
    website: enriched.website || current.website,
    linkedinURL: enriched.linkedinURL || current.linkedinURL,
    industry: enriched.industry || current.industry,
    companyType: enriched.companyType || current.companyType,
    employeeRange: enriched.employeeRange || current.employeeRange,
    country: enriched.country || current.country,
    city: enriched.city || current.city,
    foundedYear: enriched.foundedYear || current.foundedYear,
    parentCompany: enriched.parentCompany || current.parentCompany,
    tags: enriched.tags.length > 0 ? enriched.tags : current.tags,
  };
}

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ClientWizard({ open, editing, onSubmit, onClose }: Props) {
  const { t, i18n } = useTranslation();

  // Phase: 'search' (phase 1) or 'form' (phase 2)
  const [phase, setPhase] = useState<'search' | 'form'>('search');
  const [searchName, setSearchName] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [enrichError, setEnrichError] = useState<string | null>(null);
  const loadingMessage = useRotatingMessage(enriching, LOADING_MESSAGES, 3000);
  const [form, setForm] = useState<ClientFormData>(EMPTY_FORM);
  const [tagInput, setTagInput] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof ClientFormData, string>>>({});

  // Reset when dialog opens/closes or editing changes
  useEffect(() => {
    if (!open) return;
    setFieldErrors({});
    setTagInput('');
    setEnrichError(null);
    setEnriching(false);
    if (editing) {
      setForm(clientToForm(editing.data));
      setSearchName('');
      setPhase('form');
    } else {
      setForm(EMPTY_FORM);
      setSearchName('');
      setPhase('search');
    }
  }, [open, editing]);

  // ---------------------------------------------------------------------------
  // Enrichment
  // ---------------------------------------------------------------------------

  const runEnrich = async (name: string) => {
    if (!name.trim()) return;
    setEnriching(true);
    setEnrichError(null);
    try {
      // Pass the active UI language so the AI writes the free-text fields
      // (description, industry, tags) in that language instead of English.
      const result = await enrichClientInfo(name, i18n.language);
      if (result.success && result.data) {
        const enriched = clientToForm({ name, ...result.data });
        setForm((prev) => mergeForms(prev, enriched));
      } else {
        setEnrichError(result.error ?? t('admin.clients.enrichFailed'));
        // Keep whatever is in the form (edit mode: the existing client's
        // fields; search phase: an empty form) — just carry the name over.
        setForm((prev) => ({ ...prev, name }));
      }
    } catch {
      setEnrichError(t('admin.clients.enrichFailed'));
      setForm((prev) => ({ ...prev, name }));
    } finally {
      setEnriching(false);
      setPhase('form');
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void runEnrich(searchName);
    }
  };

  // ---------------------------------------------------------------------------
  // Form field helpers
  // ---------------------------------------------------------------------------

  const setField = <K extends keyof ClientFormData>(key: K, value: ClientFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((prev) => ({ ...prev, [key]: undefined }));
    }
  };

  const addTag = () => {
    const tag = tagInput.trim();
    if (tag && !form.tags.includes(tag)) {
      setField('tags', [...form.tags, tag]);
    }
    setTagInput('');
  };

  const removeTag = (tag: string) => {
    setField('tags', form.tags.filter((t) => t !== tag));
  };

  const handleTagKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag();
    }
  };

  // ---------------------------------------------------------------------------
  // Validation + submit
  // ---------------------------------------------------------------------------

  const validate = (): boolean => {
    const errors: Partial<Record<keyof ClientFormData, string>> = {};
    if (!form.name.trim()) errors.name = t('admin.clients.nameRequired');
    if (form.website.trim()) {
      try {
        const parsed = new URL(form.website);
        if (!/^https?:$/.test(parsed.protocol)) errors.website = t('admin.clients.websiteProtocol');
      } catch {
        errors.website = t('admin.clients.websiteInvalid');
      }
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSubmitting(true);
    try {
      await onSubmit({
        ...form,
        name: form.name.trim(),
      });
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render — Phase 1: Search
  // ---------------------------------------------------------------------------

  const renderSearch = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 420,
        gap: 3,
        px: 4,
      }}
    >
      <Typography variant="h3" sx={{ fontWeight: 700, textAlign: 'center' }}>
        {t('admin.clients.new')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 480 }}>
        {t('admin.clients.enrichHint')}
      </Typography>

      <TextField
        label={t('admin.clients.name')}
        value={searchName}
        onChange={(e) => setSearchName(e.target.value)}
        onKeyDown={handleSearchKeyDown}
        fullWidth
        autoFocus
        disabled={enriching}
        sx={{ maxWidth: 480, '& input': { fontSize: 18 } }}
      />

      {enriching && (
        <Box sx={{ width: '100%', maxWidth: 480, textAlign: 'center' }}>
          <LinearProgress sx={{ mb: 1.5 }} />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontStyle: 'italic', minHeight: 24, transition: 'opacity 0.3s' }}
          >
            {loadingMessage}
          </Typography>
        </Box>
      )}

      <Button
        variant="contained"
        startIcon={<AutoAwesomeIcon />}
        onClick={() => void runEnrich(searchName)}
        disabled={!searchName.trim() || enriching}
        sx={{
          backgroundColor: colors.yellow,
          color: '#000',
          '&:hover': { backgroundColor: colors.yellowHover },
          minWidth: 220,
        }}
      >
        {t('admin.clients.enrichWithAI')}
      </Button>

      <Button
        variant="text"
        color="inherit"
        onClick={() => {
          setForm({ ...EMPTY_FORM, name: searchName });
          setPhase('form');
        }}
        disabled={enriching}
        sx={{ color: colors.textMuted, fontSize: 13 }}
      >
        {t('admin.clients.skipManual')}
      </Button>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Render — Phase 2: Form
  // ---------------------------------------------------------------------------

  const renderForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
      {enrichError && (
        <Alert severity="warning" onClose={() => setEnrichError(null)}>
          {enrichError}
        </Alert>
      )}

      {/* Header row: Avatar + Name + Code */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Avatar
          src={form.logoURL || undefined}
          sx={{
            width: 64,
            height: 64,
            backgroundColor: colors.lightGray,
            color: colors.bodyText,
            fontWeight: 700,
            fontSize: 22,
            flexShrink: 0,
            mt: 1,
          }}
        >
          {/* Always render initials as children: MUI Avatar shows them when
              `src` is unset OR when the image fails to load. */}
          {getInitials(form.name)}
        </Avatar>
        <TextField
          label={t('admin.clients.name')}
          value={form.name}
          onChange={(e) => setField('name', e.target.value)}
          error={Boolean(fieldErrors.name)}
          helperText={fieldErrors.name}
          required
          sx={{ flex: 1 }}
        />
      </Box>

      {/* Description */}
      <TextField
        label={t('admin.clients.description')}
        value={form.description}
        onChange={(e) => setField('description', e.target.value)}
        multiline
        rows={2}
        fullWidth
      />

      {/* Two-column grid */}
      <Grid container spacing={2}>
        {/* Left column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('admin.clients.website')}
              value={form.website}
              onChange={(e) => setField('website', e.target.value)}
              error={Boolean(fieldErrors.website)}
              helperText={fieldErrors.website}
              placeholder="https://example.com"
              fullWidth
            />
            <TextField
              label={t('admin.clients.linkedinURL')}
              value={form.linkedinURL}
              onChange={(e) => setField('linkedinURL', e.target.value)}
              placeholder="https://linkedin.com/company/..."
              fullWidth
            />
            <TextField
              label={t('admin.clients.country')}
              value={form.country}
              onChange={(e) => setField('country', e.target.value)}
              fullWidth
            />
            <TextField
              label={t('admin.clients.city')}
              value={form.city}
              onChange={(e) => setField('city', e.target.value)}
              fullWidth
            />
          </Box>
        </Grid>

        {/* Right column */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('admin.clients.companyType')}
              value={form.companyType}
              onChange={(e) => setField('companyType', e.target.value as CompanyType | '')}
              select
              fullWidth
            >
              <MenuItem value="">—</MenuItem>
              {COMPANY_TYPES.map((ct) => (
                <MenuItem key={ct} value={ct}>
                  {t(`admin.clients.companyTypes.${ct}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('admin.clients.industry')}
              value={form.industry}
              onChange={(e) => setField('industry', e.target.value)}
              fullWidth
            />
            <TextField
              label={t('admin.clients.employeeRange')}
              value={form.employeeRange}
              onChange={(e) => setField('employeeRange', e.target.value as EmployeeRange | '')}
              select
              fullWidth
            >
              <MenuItem value="">—</MenuItem>
              {EMPLOYEE_RANGES.map((er) => (
                <MenuItem key={er} value={er}>
                  {er}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('admin.clients.foundedYear')}
              value={form.foundedYear}
              onChange={(e) => setField('foundedYear', e.target.value)}
              placeholder="2005"
              fullWidth
            />
            <TextField
              label={t('admin.clients.parentCompany')}
              value={form.parentCompany}
              onChange={(e) => setField('parentCompany', e.target.value)}
              fullWidth
            />
          </Box>
        </Grid>
      </Grid>

      {/* Tags */}
      <Box>
        <Typography variant="caption" sx={{ color: colors.textMuted, mb: 0.5, display: 'block' }}>
          {t('admin.clients.tags')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
          {form.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              size="small"
              onDelete={() => removeTag(tag)}
            />
          ))}
        </Box>
        <TextField
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onKeyDown={handleTagKeyDown}
          onBlur={addTag}
          placeholder={t('admin.clients.addTag')}
          size="small"
          sx={{ width: 220 }}
        />
      </Box>

      {/* Logo URL */}
      <TextField
        label={t('admin.clients.logoURL')}
        value={form.logoURL}
        onChange={(e) => setField('logoURL', e.target.value)}
        placeholder="https://..."
        fullWidth
      />

      {/* Re-enrich with AI — available in both create and edit modes */}
      <Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => void runEnrich(form.name)}
          disabled={!form.name.trim() || enriching}
          sx={{ borderColor: colors.yellow, color: colors.bodyText }}
        >
          {t('admin.clients.reEnrichWithAI')}
        </Button>
        {enriching && <LinearProgress sx={{ mt: 1 }} />}
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Dialog shell
  // ---------------------------------------------------------------------------

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (submitting || enriching) return;
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') onClose();
      }}
      fullScreen
      slotProps={{ paper: { sx: { borderRadius: 0 } } }}
    >
      <DialogContent
        sx={{
          maxWidth: 860,
          width: '100%',
          mx: 'auto',
          px: { xs: 2, md: 4 },
          py: 4,
        }}
      >
        {phase === 'search' ? renderSearch() : renderForm()}
      </DialogContent>

      {phase === 'form' && (
        <DialogActions
          sx={{
            borderTop: `1px solid ${colors.lightGray}`,
            px: 4,
            py: 2,
            justifyContent: 'flex-end',
            gap: 1,
          }}
        >
          <Button variant="outlined" color="secondary" onClick={onClose} disabled={submitting || enriching}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => void handleSave()}
            disabled={submitting || enriching}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      )}

      {phase === 'search' && (
        <DialogActions
          sx={{
            borderTop: `1px solid ${colors.lightGray}`,
            px: 4,
            py: 2,
            justifyContent: 'flex-start',
          }}
        >
          <Button variant="outlined" color="secondary" onClick={onClose} disabled={enriching}>
            {t('common.cancel')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
